import { useCallback, useEffect, useState } from 'react';
import { KeyRound, Save, Trash2, ShieldCheck, AlertCircle } from 'lucide-react';
import { pb, formatWhen, errorText } from '../../lib/pb';
import { SECRET_KEYS, type SecretMeta } from '../../../../shared/types';
import { confirmDialog } from '../ConfirmDialog';

const DESCRIPTIONS: Record<string, string> = {
  KIE_API_KEY: 'kie.ai — Nano Banana images and Veo video.',
  GEMINI_API_KEY: 'Google AI Studio — script writing and subtitle correction.',
  GROQ_API_KEY: 'Groq — Whisper transcription for caption timing.',
  OPENROUTER_API_KEY:
    'OpenRouter — paid fallback for script writing and subtitle correction when the Google free tier is rate limited. Optional: leave empty to use Google only.',
};

/**
 * API keys.
 *
 * Editable here, never readable here. Values are AES-encrypted in the database
 * and the `secrets` collection denies every rule, so the only reader is the
 * worker via a token-guarded route. Replacing a key takes effect on the next
 * job — no redeploy, and no key ever reaches a browser.
 */
export default function SecretsEditor() {
  const [items, setItems] = useState<SecretMeta[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/secrets', {
        headers: { Authorization: pb.authStore.token },
      });
      if (!res.ok) throw new Error(await res.text());
      setItems((await res.json()) as SecretMeta[]);
      setError(null);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (key: string) => {
    const value = values[key]?.trim();
    if (!value) return;

    setBusy(key);
    setError(null);
    try {
      const res = await fetch(`/api/admin/secrets/${encodeURIComponent(key)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: pb.authStore.token },
        body: JSON.stringify({ value }),
      });
      if (!res.ok) throw new Error(await res.text());
      setValues((v) => ({ ...v, [key]: '' }));
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (key: string) => {
    if (
      !(await confirmDialog({
        title: `Delete ${key}?`,
        message: 'Any pipeline stage that needs it will fail until it is set again.',
        confirmLabel: 'Delete key',
        danger: true,
      }))
    ) {
      return;
    }
    setBusy(key);
    try {
      const res = await fetch(`/api/admin/secrets/${encodeURIComponent(key)}`, {
        method: 'DELETE',
        headers: { Authorization: pb.authStore.token },
      });
      if (!res.ok) throw new Error(await res.text());
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  // Known keys first, then anything else that has been added directly.
  const known = SECRET_KEYS.map((key) => ({
    key: key as string,
    meta: items.find((i) => i.key === key) ?? null,
  }));
  const extra = items.filter((i) => !SECRET_KEYS.includes(i.key as never));

  return (
    <div className="space-y-4">
      <div className="zen-card p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck size={18} className="text-natural-sage shrink-0 mt-0.5" />
          <div className="text-xs text-natural-text/70 leading-relaxed">
            <p className="font-semibold text-natural-text mb-1">Write-only by design</p>
            <p>
              Keys are encrypted before storage and are never sent back to a browser — not even
              yours. You can replace a key, but you cannot read one. If you lose one, get a fresh
              key from the provider and paste it here.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="zen-card p-4 text-xs text-red-600 flex items-start gap-2">
          <AlertCircle size={14} className="shrink-0 mt-0.5" />
          <span className="break-words">{error}</span>
        </div>
      )}

      {loading && <div className="zen-card p-8 text-xs text-natural-text/45">Loading…</div>}

      {!loading &&
        [...known, ...extra.map((meta) => ({ key: meta.key, meta }))].map(({ key, meta }) => (
          <div key={key} className="zen-card p-5 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <KeyRound size={14} className="text-natural-text/40" />
                  <h3 className="font-mono text-sm font-semibold">{key}</h3>
                  {meta ? (
                    <span className="zen-pill bg-emerald-50 text-emerald-700">Set</span>
                  ) : (
                    <span className="zen-pill bg-amber-50 text-amber-700">Missing</span>
                  )}
                </div>
                {DESCRIPTIONS[key] && (
                  <p className="text-[11px] text-natural-text/55 mt-1">{DESCRIPTIONS[key]}</p>
                )}
                {meta && (
                  <p className="text-[11px] text-natural-text/45 mt-1 font-mono">
                    {meta.hint} · updated {formatWhen(meta.updated)}
                    {meta.updated_by ? ` by ${meta.updated_by}` : ''}
                  </p>
                )}
              </div>

              {meta && (
                <button
                  onClick={() => void remove(key)}
                  disabled={busy === key}
                  className="zen-btn-danger py-1.5 px-2.5 text-[11px]"
                >
                  <Trash2 size={11} />
                </button>
              )}
            </div>

            <div className="flex gap-2">
              <input
                type="password"
                autoComplete="new-password"
                value={values[key] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                placeholder={meta ? 'Paste a new key to replace it' : 'Paste the key'}
                className="zen-input font-mono text-xs flex-1"
              />
              <button
                onClick={() => void save(key)}
                disabled={!values[key]?.trim() || busy === key}
                className="zen-btn-primary py-2 px-4 text-xs shrink-0"
              >
                <Save size={13} />
                {busy === key ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        ))}
    </div>
  );
}
